import api from '../services/api';

export async function getSpecifications(params = null) {
  try { 
    const response = await api.get('/api/specifications', { params }); 
    
    // Handle API response format: { status: 1, message: "Success", data: { data: [...], meta: {...} } }
    if (response.data && response.data.status === 1 && response.data.data) {
      // Ensure all specifications have required fields with default values
      if (response.data.data.data && Array.isArray(response.data.data.data)) {
 
        
        // Add the department if missing in the API response
        response.data.data.data = response.data.data.data.map(spec => { 
          // Only provide defaults for truly missing fields (undefined/null)
          return {
            ...spec,
            // Only add a default for requisition_status if it's missing
            requisition_status: spec.requisition_status !== undefined ? spec.requisition_status : 0
          };
        });
 
      }
      return response.data;
    } else {
      // Fallback for direct data format
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching specifications:', error);
    throw error;
  }
}

export async function getSpecification(id) {
  try {
    const response = await api.get(`/api/specifications/${id}`);
    
    // Add default values for critical fields
    const ensureRequiredFields = (data) => {
      if (!data) return data;
      
      if (data.specification) {
     
        if (data.specification.department === 0 || data.specification.department === null || data.specification.department === undefined) {
          console.log('⚠️ ALERT: Department is missing or zero in single specification');
        }
        
        data.specification = {
          ...data.specification,
          // Don't set a default for department as it's an important field
          // Only add defaults for requisition_status if it's missing
          requisition_status: data.specification.requisition_status !== undefined ? 
                              data.specification.requisition_status : 0
        }; 
      }
      return data;
    };
    
    // Handle API response format: { status: 1, message: "Success", data: { specification, submissions, sent } }
    if (response.data && response.data.status === 1 && response.data.data) {
      return ensureRequiredFields(response.data.data);
    } else if (response.data && response.data.status === 1) {
      // Handle format where data is at root level
      return ensureRequiredFields(response.data);
    } else {
      // Fallback for direct data format
      return ensureRequiredFields(response.data);
    }
  } catch (error) {
    console.error(`Error fetching specification ${id}:`, error);
    throw error;
  }
}

export async function createSpecification(specificationData) {
  try {
 
    // Verify department is being sent
    if (specificationData.department === undefined || specificationData.department === null || specificationData.department === 0) {
      console.warn('⚠️ WARNING: Creating specification with missing or zero department:', specificationData.department);
    } else {
      console.log('✅ Department value being sent:', specificationData.department);
    }
    
    const response = await api.post('/api/specifications', specificationData);
 
    return response.data;
  } catch (error) {
    console.error('Error creating specification:', error);
    throw error;
  }
}

export async function updateSpecification(id, specificationData) {
  try {
    const response = await api.put(`/api/specifications/${id}`, specificationData);
    return response.data;
  } catch (error) {
    console.error(`Error updating specification ${id}:`, error);
    throw error;
  }
}

export async function deleteSpecification(id) {
  try {
    const response = await api.delete(`/api/specifications/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting specification ${id}:`, error);
    throw error;
  }
}

export async function getFormData() {
  try {
    const response = await api.get('/api/specifications/create');
    return response.data;
  } catch (error) {
    console.error('Error fetching form data:', error);
    throw error;
  }
}

export async function getSuppliers() {
  try {
    const response = await api.get('/api/specifications/suppliers');
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    throw error;
  }
}

export async function getSupplierNames(supplierCodes) {
  try {
    if (!supplierCodes || !Array.isArray(supplierCodes) || supplierCodes.length === 0) {
      return {};
    }

    // Extract supplier codes from possible objects or strings
    const codes = supplierCodes.map(code => {
      if (typeof code === 'object' && code !== null) {
        return code.BP_Code || code.value || code.id || code;
      }
      return code;
    });

    // Make a POST request to get supplier names by codes
    const response = await api.post('/api/specifications/supplier-names', { codes });
    
    // Return a mapping of supplier code to name
    return response.data.data || {};
  } catch (error) {
    console.error('Error fetching supplier names:', error);
    // Return empty object on error so UI doesn't break
    return {};
  }
}

export async function getWorkflows() {
  try {
    const response = await api.get('/api/specifications/workflows');
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching workflows:', error);
    throw error;
  }
}

export async function getBudgetItems() {
  try {
    const response = await api.get('/api/specifications/budget_items');
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching budget items:', error);
    throw error;
  }
}

/**
 * Get budget items filtered by GL code
 * @param {string} glCode - The GL code to filter by
 * @param {number} year - Optional budget year
 * @param {string} month - Optional budget month
 * @returns {Promise<Array>} Budget items with allocation info
 */
export async function getBudgetItemsByGl(glCode, year = null, month = null) {
  try {
    const params = {};
    if (year) params.year = year;
    if (month) params.month = month;
    
    const response = await api.get(`/api/specifications/budget_items/${encodeURIComponent(glCode)}`, { params });
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching budget items by GL:', error);
    throw error;
  }
}

export async function submitComment(id, commentData) {
  try {
    const response = await api.post(`/api/specifications/${id}/comment`, commentData);
    return response.data;
  } catch (error) {
    console.error(`Error submitting comment for specification ${id}:`, error);
    throw error;
  }
}

// Save evaluation function for specification model
export const saveEvaluation = async (quotationId, values) => {
  try {
    // Convert to string to prevent JavaScript number precision loss with large IDs
    const response = await api.post('/api/specifications/evaluation', {
      quotation_id: String(quotationId),
      values: values
    });
    return response.data;
  } catch (error) {
    console.error('Error saving evaluation:', error);
    throw error;
  }
};

// Update only the suppliers for a specification (dedicated endpoint to avoid data loss)
export async function updateSpecificationSuppliers(specificationId, supplierCodes) {
  try {
    const response = await api.put(`/api/specifications/${specificationId}/suppliers`, {
      BP_Code: supplierCodes
    });
    return response.data;
  } catch (error) {
    console.error('Error updating specification suppliers:', error);
    throw error;
  }
}
